import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  ExternalIntegrationCall,
  ExternalIntegrationCallPayload,
  ExternalIntegrationCallStatus,
} from 'dashboard/models/external-integration-calls';
import {
  clearExternalIntegrationCall,
  setExternalIntegrationCall,
} from 'dashboard/redux/slices/external-integration-call';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiRequestError from 'lib/errors/ApiRequestError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

interface RunQueryAndPollParams {
  externalIntegrationId: string;
  shouldContinue?: (externalIntegrationCallId: string) => boolean;
  sql: string;
  limit?: number;
  offset?: number;
  onError?: (e: Error) => void;
  onNetworkError?: () => void;
  onQueryError?: () => void;
  onQuerySubmitted?: (call: ExternalIntegrationCall) => void;
  onQueryCompleted?: () => void;
}

const poll = async ({
  externalIntegrationCallId,
}: {
  externalIntegrationCallId: string;
}): Promise<ExternalIntegrationCall> => {
  const response = await SessionClientDashboard.getResource<ExternalIntegrationCall>(
    ApiEndpoint.ExternalIntegrationCall,
    externalIntegrationCallId,
  );
  return response.data;
};

const pollContinuously = async ({
  dispatch,
  externalIntegrationCallId,
  shouldContinue = (_: string) => true,
  onError = (_) => null,
  onNetworkError = (_) => null,
  onQueryError = () => null,
  onQueryCompleted = () => null,
}: {
  dispatch: Dispatch;
  externalIntegrationCallId: string;
  shouldContinue: (externalIntegrationCallId: string) => boolean;
  onError?: (e: Error) => void;
  onNetworkError?: (e: Error) => void;
  onQueryError?: () => void;
  onQueryCompleted?: () => void;
}): Promise<ExternalIntegrationCall | null> => {
  /* eslint-disable no-constant-condition */
  while (true) {
    let updatedCall;
    try {
      updatedCall = await poll({ externalIntegrationCallId });
    } catch (err) {
      if (err instanceof ApiNetworkError || err instanceof ApiRequestError) {
        onNetworkError(err);
      } else if (err instanceof ApiResponseError) {
        if (onError) {
          onError(err);
        } else {
          onNetworkError(err);
        }
      }
      return null;
    }

    if (!shouldContinue(externalIntegrationCallId)) {
      return null;
    }

    switch (updatedCall.attributes?.status) {
      case ExternalIntegrationCallStatus.Completed:
        onQueryCompleted();
        break;
      case ExternalIntegrationCallStatus.Processing:
        await new Promise((resolve) => setTimeout(resolve, 1000));
        break;
      default:
        onQueryError();
        break;
    }
    dispatch(setExternalIntegrationCall(updatedCall));
  }
};

export const runQueryAndPoll = ({
  shouldContinue = (_: string) => true,
  onError = (_: Error) => null,
  onNetworkError = () => null,
  onQueryError = () => null,
  onQuerySubmitted = (_) => null,
  onQueryCompleted = () => null,
  externalIntegrationId,
  sql,
  limit = 1,
  offset,
}: RunQueryAndPollParams) => {
  return async (dispatch: Dispatch) => {
    let response;
    try {
      response = await SessionClientDashboard.postResourceCreate<
        ExternalIntegrationCall,
        ExternalIntegrationCallPayload
      >({
        type: ApiModel.ExternalIntegrationCall,
        attributes: {
          'external-integration-id': externalIntegrationId,
          query: {
            sql,
            options: {
              limit,
              offset,
            },
          },
        },
      });
    } catch (err) {
      if (err instanceof ApiNetworkError || err instanceof ApiRequestError) {
        onNetworkError();
      } else if (err instanceof ApiResponseError) {
        if (onError) {
          onError(err);
        } else {
          onNetworkError();
        }
      }
      dispatch(clearExternalIntegrationCall());
      return null;
    }

    const externalIntegrationCall = response.data;
    onQuerySubmitted(externalIntegrationCall);

    if (!shouldContinue(externalIntegrationCall.id)) {
      return null;
    }

    switch (externalIntegrationCall.attributes?.status) {
      case ExternalIntegrationCallStatus.Processing:
        return await pollContinuously({
          dispatch: dispatch,
          externalIntegrationCallId: externalIntegrationCall.id,
          shouldContinue,
          onQueryCompleted,
          onQueryError,
          onError,
          onNetworkError,
        });
      case ExternalIntegrationCallStatus.Completed:
        onQueryCompleted();
        break;
      case ExternalIntegrationCallStatus.Errored:
        onQueryError();
        break;
      case ExternalIntegrationCallStatus.Cancelled:
        break;
      case ExternalIntegrationCallStatus.TimedOut:
        break;
      default:
        onQueryError();
        break;
    }
    dispatch(setExternalIntegrationCall(externalIntegrationCall));
    return null;
  };
};
